import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import PdfInlineViewer from './shared/PdfInlineViewer';
import './DocumentsSplitPane.css';

/**
 * How a child asks the pane to show a document beside the record.
 *
 * ``null`` outside a pane is deliberate and is the whole reason this is a
 * context rather than a prop: the contracts panel is hosted BOTH inside this
 * pane (the delivery) and on a screen with no pane at all (the Fuel Enquiry
 * form). A child reads this, gets null there, and keeps its pop-up. Passing a
 * prop down would have meant every host remembering to pass it, and the one
 * that forgot would silently go back to covering its own screen.
 */
const DocumentViewerContext = createContext(null);

export const useDocumentViewer = () => useContext(DocumentViewerContext);

/**
 * The right-hand column of a "[ record | paperwork ]" split screen.
 *
 * Marcel (Sept feedback): "Contracts and Bunker notes need split screen
 * [Delivery | Cont/Notes] so a user can check side by side." Until now the
 * delivery's paperwork was a COVER — a full-screen dark overlay with a drawer
 * on top of it — so reading a contract meant losing sight of the delivery you
 * were checking it against.
 *
 * This is deliberately its own component and deliberately knows nothing about
 * deliveries. The same pane is wanted next on the enquiry and the freight
 * recap, and the host screen supplies its own body as ``children`` — the
 * contracts list, an upload explorer, notes, whatever that record's paperwork
 * is. Putting it inside a host screen would have meant three copies that drift
 * apart in ways nobody can see: each would still render, just with a different
 * idea of how a pane closes or how wide it may get.
 *
 * Nothing inside renders until the pane is open. That is not a nicety: the
 * body's children are what fetch the contracts and the document list, so
 * mounting them eagerly would put two network calls on the critical path of
 * every delivery load, for a pane most people never open.
 */
const DocumentsSplitPane = ({ isOpen, onClose, title, children }) => {
    // Escape closes the pane, matching every other panel on the screen. Bound
    // only while open so a closed pane cannot swallow another panel's Escape.
    useEffect(() => {
        if (!isOpen) return undefined;
        const handleEsc = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    // The document currently being read in this pane, if any.
    const [viewing, setViewing] = useState(null);

    // Closing the pane must not leave a document "open" behind it, or
    // reopening the pane lands on a stale file instead of the list.
    useEffect(() => {
        if (!isOpen) setViewing(null);
    }, [isOpen]);

    const viewer = useMemo(() => ({
        open: (url, name) => setViewing({ url, name }),
        close: () => setViewing(null),
    }), []);

    if (!isOpen) return null;

    return (
        <DocumentViewerContext.Provider value={viewer}>
            <aside className="documents-split-pane" aria-label={title}>
                <div className="documents-split-pane-header">
                    <h2 className="documents-split-pane-title">
                        {viewing ? viewing.name || 'Document' : title}
                    </h2>
                    <button
                        type="button"
                        className="documents-split-pane-close"
                        onClick={onClose}
                        aria-label="Close panel"
                    >
                        &times;
                    </button>
                </div>
                {/* The list stays MOUNTED underneath while a document is read,
                    just hidden. Unmounting it would throw away the fetched
                    contracts and the scroll position, so "back to list" would
                    reload the pane every time — which is the sort of thing that
                    makes people stop using a feature. */}
                <div
                    className="documents-split-pane-body"
                    hidden={Boolean(viewing)}
                >
                    {children}
                </div>
                {viewing && (
                    <div className="documents-split-pane-body documents-split-pane-viewing">
                        <PdfInlineViewer
                            pdfUrl={viewing.url}
                            fileName={viewing.name}
                            onClose={viewer.close}
                        />
                    </div>
                )}
            </aside>
        </DocumentViewerContext.Provider>
    );
};

DocumentsSplitPane.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    children: PropTypes.node,
};

DocumentsSplitPane.defaultProps = {
    isOpen: false,
    title: 'Documents',
    children: null,
};

export default DocumentsSplitPane;
